import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Upload, Send, BarChart3, RefreshCw, ArrowRight, CheckCircle2 } from 'lucide-react';
interface SlideOperatingModelProps {
  onContinue: () => void;
}
interface Step {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  items: string[];
  micro: string;
}
export function SlideOperatingModel({
  onContinue
}: SlideOperatingModelProps) {
  const [activeStep, setActiveStep] = useState(-1);
  const [showInsight, setShowInsight] = useState(false);
  const steps: Step[] = [{
    icon: <Upload className="w-5 h-5" data-id="element-216" />,
    label: 'Input',
    sublabel: 'Content-agnostic',
    items: ['Any digital asset', 'Slides, PDFs, video, data'],
    micro: 'We work with what you already have'
  }, {
    icon: <Send className="w-5 h-5" data-id="element-217" />,
    label: 'Deploy',
    sublabel: 'Channel-agnostic',
    items: ['Email', 'WhatsApp', 'Congress', 'Web / portals'],
    micro: 'Start the journey anywhere'
  }, {
    icon: <BarChart3 className="w-5 h-5" data-id="element-218" />,
    label: 'Learn',
    sublabel: 'Deep engagement data',
    items: ['What HCPs click', 'What they skip', 'What drives decisions'],
    micro: 'Quantitative + qualitative insight'
  }, {
    icon: <RefreshCw className="w-5 h-5" data-id="element-219" />,
    label: 'Improve',
    sublabel: 'Continuous optimization',
    items: ['Refine content', 'Adjust flow', 'Increase engagement'],
    micro: 'Driven by actionable data'
  }];
  useEffect(() => {
    const timers = steps.map((_, idx) => setTimeout(() => setActiveStep(idx), 800 + idx * 900));
    const insightTimer = setTimeout(() => setShowInsight(true), 800 + steps.length * 900 + 500);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(insightTimer);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-220">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-8 text-center" data-id="element-221">
        What does a scalable model{' '}
        <span className="text-teal-400" data-id="element-222">actually</span> look like?
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-223">
        {/* Loop — 4 Steps */}
        <div className="lg:col-span-8 relative" data-id="element-224">
          {/* Connection lines */}
          <div className="absolute top-12 left-[12.5%] right-[12.5%] h-[2px] bg-navy-600 z-0" data-id="element-225">
            <motion.div className="h-full bg-teal-400/50" initial={{
            width: '0%'
          }} animate={{
            width: activeStep >= 3 ? '100%' : `${(activeStep + 1) / 4 * 100}%`
          }} transition={{
            duration: 0.5
          }} data-id="element-226" />
          </div>

          <div className="grid grid-cols-4 gap-3 relative z-10" data-id="element-227">
            {steps.map((step, idx) => {
            const isActive = idx <= activeStep;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: isActive ? 1 : 0.25,
              y: isActive ? 0 : 20
            }} transition={{
              duration: 0.5
            }} className="flex flex-col items-center text-center" data-id="element-228">
                  {/* Icon circle */}
                  <motion.div animate={{
                backgroundColor: isActive ? '#00D4AA' : '#1E2D4A',
                borderColor: isActive ? '#00D4AA' : '#2A3F5F',
                scale: isActive ? 1 : 0.85
              }} className="w-10 h-10 rounded-full border-3 flex items-center justify-center text-navy-900 mb-4 shadow-lg" data-id="element-229">
                    {step.icon}
                  </motion.div>

                  {/* Card */}
                  <div className={`rounded-xl p-4 w-full transition-all duration-500 ${isActive ? 'bg-navy-700/60 border border-navy-500' : 'bg-navy-800/40 border border-navy-700/50'}`} data-id="element-230">
                    <h4 className={`font-semibold text-sm mb-0.5 ${isActive ? 'text-teal-400' : 'text-white/40'}`} data-id="element-231">
                      {step.label}
                    </h4>
                    <p className={`text-xs mb-3 ${isActive ? 'text-white/50' : 'text-white/20'}`} data-id="element-232">
                      {step.sublabel}
                    </p>

                    <div className="space-y-1.5 mb-3" data-id="element-233">
                      {step.items.map((item, i) => <div key={i} className={`text-xs flex items-center gap-1.5 ${isActive ? 'text-white/80' : 'text-white/30'}`} data-id="element-234">
                          <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isActive ? 'bg-teal-400' : 'bg-navy-600'}`} data-id="element-235" />
                          {item}
                        </div>)}
                    </div>

                    <p className={`text-xs italic border-t pt-2 ${isActive ? 'text-teal-400/70 border-navy-500' : 'text-white/20 border-navy-700'}`} data-id="element-236">
                      {step.micro}
                    </p>
                  </div>

                  {/* Arrow between steps */}
                  {idx < steps.length - 1 && <motion.div className="absolute top-12 z-20" style={{
                left: `${(idx + 1) * 25}%`,
                transform: 'translate(-50%, -50%)'
              }} animate={{
                opacity: idx < activeStep ? 0.6 : 0
              }} data-id="element-237">
                      <ArrowRight className="w-4 h-4 text-teal-400" data-id="element-238" />
                    </motion.div>}
                </motion.div>;
          })}
          </div>

          {/* Loop-back arrow */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: activeStep >= 3 ? 1 : 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="flex items-center justify-center mt-4 gap-2" data-id="element-239">
            <motion.div animate={{
            rotate: activeStep >= 3 ? 360 : 0
          }} transition={{
            repeat: Infinity,
            duration: 6,
            ease: 'linear'
          }} data-id="element-240">
              <RefreshCw className="w-5 h-5 text-teal-400/60" data-id="element-241" />
            </motion.div>
            <span className="text-xs text-teal-400/60 uppercase tracking-widest font-medium" data-id="element-242">
              Improve → Input · Continuous loop
            </span>
          </motion.div>
        </div>

        {/* Insight Box */}
        <div className="lg:col-span-4" data-id="element-243">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: showInsight ? 1 : 0,
          x: showInsight ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-600 p-6 rounded-2xl backdrop-blur-sm" data-id="element-244">
            <div className="text-teal-400 font-medium mb-4 flex items-center gap-2 text-sm" data-id="element-245">
              <CheckCircle2 className="w-4 h-4" data-id="element-246" />
              Insight
            </div>
            <div className="text-lg leading-relaxed text-white/90 space-y-3" data-id="element-247">
              <p className="font-medium" data-id="element-248">This is not campaign execution.</p>
              <p className="text-white/70" data-id="element-249">
                <span className="text-teal-400 mr-1" data-id="element-250">→</span>
                It's continuous, data-driven improvement
              </p>
              <p className="text-white/70" data-id="element-251">
                <span className="text-teal-400 mr-1" data-id="element-252">→</span>
                Every interaction makes the next one better
              </p>
            </div>

            <div className="mt-6" data-id="element-253">
              <Button onClick={onContinue} data-id="element-254">Continue</Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}